import { useEffect, useMemo, useState } from "react";
import { api } from "@/lib/api";
import { formatMoney } from "@/lib/format";
import { Card, CardContent } from "@/components/ui/card";
import { toast } from "sonner";
import { Loader2, ArrowLeft } from "lucide-react";

const PERIODS = [
  { id: "all", label: "All Time" },
  { id: "30d", label: "30 Days" },
  { id: "90d", label: "90 Days" },
  { id: "mtd", label: "Month to Date" },
  { id: "prev_month", label: "Previous Month" },
  { id: "t12m", label: "12 Months" },
];

const BANDS = {
  active: { label: "Active", cls: "text-status-available" },
  cooling: { label: "Cooling", cls: "text-terracotta" },
  inactive: { label: "Inactive", cls: "text-status-sold" },
  never_active: { label: "No business activity yet", cls: "text-muted-foreground" },
};

const fmtSqft = (n) => (Number.isFinite(n) ? `${n.toLocaleString(undefined, { maximumFractionDigits: 1 })} sq ft` : "—");
const fmtDate = (iso) => (iso ? new Date(iso).toLocaleDateString() : "—");
const fmtDays = (d) => (d === null || d === undefined ? "—" : `${d}d`);
const fmtPct = (r) => (r === null || r === undefined ? "—" : `${Math.round(r * 100)}%`);
const fmtMoneyOr = (v) => (v === null || v === undefined ? "—" : formatMoney(v));
const fmtNumOr = (v) => (v === null || v === undefined ? "—" : v);

function Stat({ testid, label, value, sub }) {
  return (
    <Card className="border-border bg-white" data-testid={testid}>
      <CardContent className="p-5">
        <p className="num text-2xl font-medium text-charcoal">{value}</p>
        <p className="mt-1 text-xs font-semibold uppercase tracking-wider text-warmgrey">{label}</p>
        {sub && <p className="mt-1 text-xs text-muted-foreground">{sub}</p>}
      </CardContent>
    </Card>
  );
}

export default function DealerAnalyticsTab({ dealer }) {
  const [period, setPeriod] = useState("all");
  const [locationId, setLocationId] = useState(null);
  const [detail, setDetail] = useState(null);
  const [locations, setLocations] = useState(null);
  const [sortKey, setSortKey] = useState("net_revenue_usd");
  const [sortDir, setSortDir] = useState("desc");
  const [engagement, setEngagement] = useState(null);

  useEffect(() => {
    api.get(`/admin/analytics/dealers/${dealer.id}/engagement?period=${period}`)
      .then((r) => setEngagement(r.data))
      .catch(() => setEngagement(null));
  }, [dealer.id, period]);

  useEffect(() => {
    const q = `period=${period}${locationId ? `&location_id=${locationId}` : ""}`;
    setDetail(null);
    api.get(`/admin/analytics/dealers/${dealer.id}?${q}`)
      .then((r) => setDetail(r.data))
      .catch(() => toast.error("Failed to load dealer analytics"));
  }, [dealer.id, period, locationId]);

  useEffect(() => {
    api.get(`/admin/analytics/dealers/${dealer.id}/locations?period=${period}`)
      .then((r) => setLocations(r.data))
      .catch(() => toast.error("Failed to load location analytics"));
  }, [dealer.id, period]);

  const onSort = (key) => {
    if (sortKey === key) setSortDir((d) => (d === "desc" ? "asc" : "desc"));
    else {
      setSortKey(key);
      setSortDir("desc");
    }
  };

  const sortedLocations = useMemo(() => {
    if (!locations) return [];
    const rows = [...locations.locations];
    rows.sort((a, b) => {
      const av = a[sortKey] ?? -Infinity;
      const bv = b[sortKey] ?? -Infinity;
      const cmp = typeof av === "number" && typeof bv === "number" ? av - bv : String(av).localeCompare(String(bv));
      return sortDir === "desc" ? -cmp : cmp;
    });
    return rows;
  }, [locations, sortKey, sortDir]);

  if (!detail || !locations) {
    return (
      <div className="flex min-h-[30vh] items-center justify-center" data-testid="dealer-analytics-loading">
        <Loader2 className="h-6 w-6 animate-spin text-terracotta" />
      </div>
    );
  }

  const ov = detail.overview;
  const cv = detail.conversions;
  const eff = detail.efficiency;
  const act = detail.activity;
  const band = BANDS[act.band] || BANDS.never_active;
  const hasActivity = ov.orders > 0 || ov.samples_shipped > 0 || ov.active_projects > 0 || ov.active_reservations > 0 || act.recent.length > 0;
  const s = cv.sample_followed_by_order;
  const p = cv.project_to_order;
  const r = cv.reservation_to_order;
  const scoped = !!locationId;

  return (
    <div data-testid="dealer-analytics-tab">
      <div className="flex flex-wrap items-center justify-between gap-4">
        <div className="flex items-center gap-3">
          {scoped && (
            <button
              type="button"
              data-testid="analytics-back-all-locations"
              onClick={() => setLocationId(null)}
              className="inline-flex items-center gap-1 text-sm font-medium text-terracotta hover:text-terracotta-hover"
            >
              <ArrowLeft className="h-4 w-4" /> All locations
            </button>
          )}
          <p className="text-sm text-muted-foreground" data-testid="analytics-scope-label">
            {scoped ? `Location: ${detail.location_label}` : "All locations"}
          </p>
        </div>
        <div className="flex flex-wrap gap-1 rounded-lg border border-border bg-white p-1" data-testid="dealer-period-selector">
          {PERIODS.map((pp) => (
            <button
              key={pp.id}
              type="button"
              data-testid={`dealer-period-${pp.id}`}
              onClick={() => setPeriod(pp.id)}
              className={`rounded-md px-3 py-1.5 text-xs transition-colors duration-200 ${
                period === pp.id ? "bg-terracotta font-medium text-white" : "text-warmgrey hover:text-charcoal"
              }`}
            >
              {pp.label}
            </button>
          ))}
        </div>
      </div>

      <h3 className="mt-6 font-display text-xl font-medium text-charcoal">Overview</h3>
      {hasActivity ? (
        <div className="mt-3 grid grid-cols-2 gap-4 md:grid-cols-3 lg:grid-cols-5" data-testid="dealer-analytics-overview">
          <Stat testid="da-net-revenue" label="Net Revenue" value={formatMoney(ov.net_revenue_usd)} />
          <Stat testid="da-orders" label="Orders" value={ov.orders} />
          <Stat testid="da-sqft" label="Sq Ft Sold" value={fmtSqft(ov.sqft_sold)} />
          <Stat testid="da-aov" label="Avg Order Value" value={formatMoney(ov.avg_order_value_usd)} />
          <Stat testid="da-samples" label="Samples Shipped" value={ov.samples_shipped} />
          <Stat testid="da-projects" label="Active Projects" value={ov.active_projects} />
          <Stat testid="da-pipeline" label="Pipeline" value={fmtSqft(ov.pipeline_sqft)} />
          <Stat testid="da-reservations" label="Active Reservations" value={ov.active_reservations} />
          <Stat testid="da-last-activity" label="Last Business Activity" value={fmtDate(ov.last_activity)} />
          <Stat testid="da-last-portal" label="Last Portal Activity" value={fmtDate(engagement?.last_portal_activity)} />
          <Stat testid="da-activity-band" label="Status" value={<span className={band.cls}>{band.label}</span>} />
        </div>
      ) : (
        <>
          <div className="mt-3 grid grid-cols-2 gap-4 md:grid-cols-3" data-testid="dealer-analytics-overview">
            <Stat testid="da-last-portal" label="Last Portal Activity" value={fmtDate(engagement?.last_portal_activity)} />
            <Stat testid="da-last-activity" label="Last Business Activity" value={fmtDate(ov.last_activity)} />
            <Stat testid="da-activity-band" label="Status" value={<span className={band.cls}>{band.label}</span>} />
          </div>
          <Card className="mt-4 border-border bg-white">
            <CardContent className="p-8 text-center text-sm text-muted-foreground" data-testid="dealer-analytics-empty">
              Analytics will populate as projects, samples, reservations and paid orders are completed.
            </CardContent>
          </Card>
        </>
      )}

      {hasActivity && (<>
      <h3 className="mt-8 font-display text-xl font-medium text-charcoal">Conversions &amp; Efficiency</h3>
      <div className="mt-3 grid grid-cols-2 gap-4 md:grid-cols-3" data-testid="dealer-analytics-conversions">
        <Stat
          testid="da-sample-followed"
          label="Sample Followed by Order"
          value={s.matured ? fmtPct(s.rate) : "—"}
          sub={s.matured ? `${s.converted} of ${s.matured} matured` : "Not enough matured sample activity yet"}
        />
        <Stat
          testid="da-project-conv"
          label="Project → Order"
          value={p.matured ? fmtPct(p.rate) : "—"}
          sub={p.matured ? `${p.converted} of ${p.matured} matured` : "Not enough matured projects yet"}
        />
        <Stat
          testid="da-reservation-conv"
          label="Reservation → Order"
          value={r.terminal ? fmtPct(r.rate) : "—"}
          sub={r.terminal ? `${r.converted} of ${r.terminal} completed` : "No completed reservations yet"}
        />
        <Stat testid="da-revenue-followed" label="Revenue Followed to Samples" value={fmtMoneyOr(cv.revenue_followed_to_samples_usd)} sub="per matured sample request" />
        <Stat testid="da-sample-cycle" label="Sample → Order Median" value={fmtDays(cv.sample_median_days)} sub={cv.sample_avg_days != null ? `avg ${cv.sample_avg_days}d` : "—"} />
        <Stat testid="da-project-cycle" label="Project → Order Median" value={fmtDays(cv.project_median_days)} sub={cv.project_avg_days != null ? `avg ${cv.project_avg_days}d` : "—"} />
      </div>
      {(s.in_progress > 0 || p.in_progress > 0 || r.in_progress > 0) && (
        <p className="mt-3 text-xs text-muted-foreground" data-testid="da-in-progress">
          In progress: {s.in_progress} sample{s.in_progress === 1 ? "" : "s"} · {p.in_progress} project{p.in_progress === 1 ? "" : "s"} · {r.in_progress} reservation{r.in_progress === 1 ? "" : "s"} (observation window still open)
        </p>
      )}
      <div className="mt-3 grid grid-cols-2 gap-4 md:grid-cols-3 lg:grid-cols-6" data-testid="dealer-analytics-efficiency">
        <Stat testid="da-orders-per-sample" label="Orders / Sample" value={fmtNumOr(eff.orders_per_sample_request)} />
        <Stat testid="da-sqft-per-order" label="Sq Ft / Order" value={fmtNumOr(eff.sqft_per_order)} />
        <Stat testid="da-orders-per-project" label="Orders / Project" value={fmtNumOr(eff.orders_per_project)} />
        <Stat testid="da-revenue-per-project" label="Revenue / Project" value={fmtMoneyOr(eff.revenue_per_project_usd)} />
        <Stat testid="da-days-activity" label="Days Since Business Activity" value={fmtDays(eff.days_since_last_activity)} />
        <Stat testid="da-days-paid" label="Days Since Paid Order" value={eff.days_since_last_paid_order === null ? "Never" : fmtDays(eff.days_since_last_paid_order)} />
      </div>

      {!scoped && (
        <>
          <h3 className="mt-8 font-display text-xl font-medium text-charcoal">Locations</h3>
          {sortedLocations.length === 0 ? (
            <Card className="mt-3 border-border bg-white"><CardContent className="p-8 text-center text-sm text-muted-foreground" data-testid="locations-empty">No locations yet.</CardContent></Card>
          ) : (
            <div className="mt-3 hidden overflow-x-auto rounded-xl border border-border bg-white md:block" data-testid="location-table">
              <table className="w-full min-w-[880px] text-sm">
                <thead className="border-b border-border bg-sand/40">
                  <tr>
                    <th className="px-4 py-3 text-left text-xs font-semibold uppercase tracking-wider text-warmgrey">Location</th>
                    <th className="px-4 py-3 text-left text-xs font-semibold uppercase tracking-wider text-warmgrey">City / State</th>
                    {[["Revenue", "net_revenue_usd"], ["Orders", "orders"], ["Sq Ft", "sqft_sold"], ["Samples", "samples_shipped"], ["Projects", "active_projects"], ["Pipeline", "pipeline_sqft"]].map(([label, key]) => (
                      <th key={key} className="px-4 py-3 text-left">
                        <button type="button" data-testid={`loc-sort-${key}`} onClick={() => onSort(key)} className={`text-xs font-semibold uppercase tracking-wider ${sortKey === key ? "text-terracotta" : "text-warmgrey hover:text-charcoal"}`}>{label}</button>
                      </th>
                    ))}
                    <th className="px-4 py-3 text-left text-xs font-semibold uppercase tracking-wider text-warmgrey">Sample Followed</th>
                    <th className="px-4 py-3 text-left text-xs font-semibold uppercase tracking-wider text-warmgrey">Last Business Activity</th>
                  </tr>
                </thead>
                <tbody>
                  {sortedLocations.map((l) => (
                    <tr
                      key={l.location_id}
                      className="cursor-pointer border-b border-border last:border-0 hover:bg-sand/30"
                      data-testid={`location-row-${l.location_id}`}
                      onClick={() => setLocationId(l.location_id)}
                    >
                      <td className="px-4 py-3 font-medium text-charcoal">{l.label}</td>
                      <td className="px-4 py-3 text-muted-foreground">{[l.city, l.state].filter(Boolean).join(", ") || "—"}</td>
                      <td className="px-4 py-3 font-medium">{formatMoney(l.net_revenue_usd)}</td>
                      <td className="px-4 py-3">{l.orders}</td>
                      <td className="px-4 py-3">{fmtSqft(l.sqft_sold)}</td>
                      <td className="px-4 py-3">{l.samples_shipped}</td>
                      <td className="px-4 py-3">{l.active_projects}</td>
                      <td className="px-4 py-3">{fmtSqft(l.pipeline_sqft)}</td>
                      <td className="px-4 py-3">{fmtPct(l.sample_followed_rate)}{l.sample_followed_in_progress > 0 ? ` (${l.sample_followed_in_progress} in progress)` : ""}</td>
                      <td className="px-4 py-3 text-muted-foreground">{fmtDate(l.last_activity)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
          {sortedLocations.length > 0 && (
            <div className="mt-3 space-y-3 md:hidden" data-testid="location-cards">
              {sortedLocations.map((l) => (
                <Card key={l.location_id} className="cursor-pointer border-border bg-white" data-testid={`location-card-${l.location_id}`} onClick={() => setLocationId(l.location_id)}>
                  <CardContent className="p-5">
                    <div className="flex items-center justify-between gap-3">
                      <p className="font-medium text-charcoal">{l.label}</p>
                      <p className="num text-sm font-medium text-charcoal">{formatMoney(l.net_revenue_usd)}</p>
                    </div>
                    <p className="mt-0.5 text-xs text-muted-foreground">{[l.city, l.state].filter(Boolean).join(", ") || "—"}</p>
                    <div className="num mt-3 grid grid-cols-3 gap-2 text-sm text-charcoal">
                      <span>{l.orders} orders</span>
                      <span>{fmtSqft(l.sqft_sold)}</span>
                      <span>{l.samples_shipped} samples</span>
                    </div>
                    <p className="mt-2 text-xs text-muted-foreground">Last business activity: {fmtDate(l.last_activity)}</p>
                  </CardContent>
                </Card>
              ))}
            </div>
          )}
          {locations.legacy_unassigned && (
            <p className="mt-2 text-xs text-muted-foreground" data-testid="legacy-location-note">
              Dealer total includes historical activity not assigned to a location.
            </p>
          )}
        </>
      )}

      <h3 className="mt-8 font-display text-xl font-medium text-charcoal">Activity</h3>
      {act.recent.length === 0 ? (
        <Card className="mt-3 border-border bg-white"><CardContent className="p-8 text-center text-sm text-muted-foreground" data-testid="activity-empty">No business activity yet</CardContent></Card>
      ) : (
        <div className="mt-3 space-y-2" data-testid="recent-activity">
          {act.recent.map((e, i) => (
            <div key={i} className="flex items-center justify-between rounded-lg border border-border bg-white px-4 py-3 text-sm" data-testid={`activity-item-${i}`}>
              <div className="flex items-center gap-3">
                <span className="font-medium text-charcoal">{e.type}</span>
                <span className="text-muted-foreground">{e.label}</span>
                {e.location_id && (
                  <span className="text-xs text-muted-foreground">
                    {(dealer.locations || []).find((x) => x.id === e.location_id)?.label || ""}
                  </span>
                )}
              </div>
              <span className="text-xs text-muted-foreground">{new Date(e.at).toLocaleString()}</span>
            </div>
          ))}
        </div>
      )}
      </>)}
    </div>
  );
}
